import api from '../../../api/client';
import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Row,
  Col,
  Statistic,
  Tag,
  Tooltip,
  message,
  Modal,
  Typography,
  Tabs,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { usePermissions } from "../../../permissions";
import config from "../../../config";
import RoleForm from "./RoleForm";
import RolePermissions from "./RolePermissions";
import PermissionManager from "./PermissionManager";

const { Text } = Typography;
const { TabPane } = Tabs;

const RoleList = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [permissionsModalVisible, setPermissionsModalVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const { createPermission, editPermission, deletePermission, viewPermission } = usePermissions();
  const environment = window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/roles`);
      setRoles(data);
    } catch (err) {
      message.error("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };
    const [activeTab, setActiveTab] = useState("roles");
  

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleDelete = (id, name) => {
    Modal.confirm({
      title: "Delete Role",
      content: `Are you sure you want to delete "${name}"?`,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await api.delete(`/roles/${id}`);
          message.success(`Role "${name}" deleted`);
          fetchRoles();
        } catch (err) {
          message.error(err.response?.data?.message || "Delete failed");
        }
      },
    });
  };

  const filteredRoles = searchText
    ? roles.filter((role) => role.name?.toLowerCase().includes(searchText.toLowerCase()))
    : roles;

  const columns = [
    { title: "ID", dataIndex: "id", key: "id", width: 80, sorter: (a, b) => a.id - b.id },
     {
      title: "Role Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => (
        <Space>
          <SafetyOutlined style={{ color: "#1677ff" }} />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => dayjs(date).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (date) => dayjs(date).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 250,
      render: (_, record) => (
        <Space>
          {viewPermission && (
            <Tooltip title="View Permissions">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => {
                  setSelectedRole(record);
                  setPermissionsModalVisible(true);
                }}
              />
            </Tooltip>
          )}
          {editPermission && (
            <Tooltip title="Edit Role">
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => {
                  setSelectedRole(record);
                  setModalVisible(true);
                }}
              />
            </Tooltip>
          )}
          {deletePermission && (
            <Tooltip title="Delete Role">
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                onClick={() => handleDelete(record.id, record.name)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
    <Card title="Role & Permisisons">
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="Roles" key="roles">

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <Card><Statistic title="Total Roles" value={roles.length} prefix={<SafetyOutlined />} /></Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card><Statistic title="Active Roles" value={roles.length} /></Card>
        </Col>
      </Row>

      <Card
        title="Role Management"
        extra={
          <Space>
            <Input.Search
              placeholder="Search roles..."
              allowClear
              style={{ width: 250 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchRoles}>Refresh</Button>
            {createPermission && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => { setSelectedRole(null); setModalVisible(true); }}>
                Add Role
              </Button>
            )}
           
          </Space>
        }
      >
        <Table columns={columns} dataSource={filteredRoles} rowKey="id" loading={loading} pagination={{ pageSize: 20 }} size="small" />
      </Card>
      </TabPane>
      <TabPane tab="Permissions" key="permissions">
<PermissionManager/>
            </TabPane>
      </Tabs>
    </Card>

      <RoleForm
        visible={modalVisible}
        onCancel={() => { setModalVisible(false); setSelectedRole(null); }}
        onSuccess={() => { setModalVisible(false); setSelectedRole(null); fetchRoles(); }}
        initialData={selectedRole}
        apiUrl={apiUrl}
      />

      <RolePermissions
        visible={permissionsModalVisible}
        onCancel={() => { setPermissionsModalVisible(false); setSelectedRole(null); }}
        role={selectedRole}
        apiUrl={apiUrl}
      />
      </>
  );
};

export default RoleList;